import React, { useState, useEffect } from 'react';
import { Modal, Card, Form, Input, Select, DatePicker, Button, Tooltip, message, Row, Col } from 'antd';
import { EditOutlined, LoadingOutlined } from '@ant-design/icons';
import Text from 'antd/lib/typography/Text';
import { startCase, debounce } from 'lodash';
import AvatarS3 from '../../../shared/images/AvatarS3';
import moment from 'moment';
import { useUserContext } from '../../../../core/components/users/useUserContext';

const { Option } = Select;
const { TextArea } = Input;

const ProjectSettingsModal = ({ visible, onCancel, project, currentUser, onSave }) => {
  const [form] = Form.useForm();
  const [isEditingInfo, setIsEditingInfo] = useState(false);
  const [isEditingTeam, setIsEditingTeam] = useState(false);
  const [isSavingInfo, setIsSavingInfo] = useState(false);
  const [isSavingTeam, setIsSavingTeam] = useState(false);

  const {
    fetchBasicUsersDetails,
    basicUsersDetails,
    basicUsersDetailsLoading,
  } = useUserContext();

  useEffect(() => {
    if (project && visible) {
      form.setFieldsValue({
        name: project.name,
        description: project.description,
        status: project.status,
        priority: project.priority,
        startDate: project.startDate ? moment(project.startDate) : null,
        endDate: project.endDate ? moment(project.endDate) : null,
        team: project.team ? project.team.map((member) => member.id) : [],
      });
    }
  }, [project, visible, form]);

  useEffect(() => {
    if (visible) {
      fetchUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const fetchUsers = debounce((name = '') => {
    if (!currentUser) return;

    fetchBasicUsersDetails({
      variables: {
        first: 100,
        skip: 0,
        where: {
          AND: [
            {
              roles_some: {
                org: {
                  id: currentUser.role.org.id,
                  level: 'PLATFORM',
                },
                roleItems_some: {
                  feature: 'TASK_MANAGEMENT',
                },
              },
              name_contains: name,
            },
          ],
        },
      },
    });
  }, 500);

  const handleSaveInfo = async () => {
    try {
      setIsSavingInfo(true);
      const values = await form.validateFields(['name', 'description', 'status', 'priority', 'startDate', 'endDate']);
      await onSave({
        ...values,
        startDate: values.startDate ? values.startDate.toISOString() : null,
        endDate: values.endDate ? values.endDate.toISOString() : null,
      });
      setIsEditingInfo(false);
      message.success('Project information updated successfully.');
    } catch (error) {
      message.error('Failed to update project information.');
      console.error('Update failed:', error);
    } finally {
      setIsSavingInfo(false);
    }
  };

  const handleSaveTeam = async () => {
    try {
      setIsSavingTeam(true);
      const values = await form.validateFields(['team']);
      await onSave({ team: values.team });
      setIsEditingTeam(false);
      message.success('Team members updated successfully.');
    } catch (error) {
      message.error('Failed to update team members.');
      console.error('Update failed:', error);
    } finally {
      setIsSavingTeam(false);
    }
  };

  const handleCancel = () => {
    setIsEditingInfo(false);
    setIsEditingTeam(false);
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title="Project Settings"
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={800}
      centered
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {/* Project Information Card */}
        <Card
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text strong style={{ fontSize: '16px' }}>Project Information</Text>
              {!isEditingInfo ? (
                <EditOutlined
                  style={{ cursor: 'pointer', fontSize: '16px', color: '#3b82f6' }}
                  onClick={() => setIsEditingInfo(true)}
                />
              ) : (
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button
                    type="primary"
                    onClick={handleSaveInfo}
                    disabled={isSavingInfo}
                    style={{
                      fontSize: '13px',
                      height: '30px',
                      borderRadius: '5px',
                      backgroundColor: '#3b82f6',
                      color: 'white',
                    }}
                  >
                    {isSavingInfo && <LoadingOutlined />} Save
                  </Button>
                  <Button
                    onClick={() => {
                      setIsEditingInfo(false);
                      form.setFieldsValue({
                        name: project.name,
                        description: project.description,
                        status: project.status,
                        priority: project.priority,
                        startDate: project.startDate ? moment(project.startDate) : null,
                        endDate: project.endDate ? moment(project.endDate) : null,
                      });
                    }}
                    disabled={isSavingInfo}
                    style={{
                      fontSize: '13px',
                      height: '30px',
                      borderRadius: '5px',
                      color: 'black',
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </div>
          }
          style={{ borderRadius: '7px' }}
        >
          <Form form={form} layout="vertical">
            {isEditingInfo ? (
              <>
                <Form.Item
                  label="Project Name"
                  name="name"
                  rules={[{ required: true, message: 'Please enter project name' }]}
                >
                  <Input placeholder="Enter project name" />
                </Form.Item>

                <Form.Item label="Description" name="description">
                  <TextArea rows={3} placeholder="Enter project description" />
                </Form.Item>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label="Status" name="status">
                      <Select placeholder="Select status">
                        <Option value="NOT_STARTED">Not Started</Option>
                        <Option value="PLANNING">Planning</Option>
                        <Option value="IN_PROGRESS">In Progress</Option>
                        <Option value="COMPLETED">Completed</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="Priority" name="priority">
                      <Select placeholder="Select priority">
                        <Option value="LOW">Low</Option>
                        <Option value="MEDIUM">Medium</Option>
                        <Option value="HIGH">High</Option>
                        <Option value="CRITICAL">Critical</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label="Start Date" name="startDate">
                      <DatePicker style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="End Date" name="endDate">
                      <DatePicker style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                </Row>
              </>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div>
                  <Text strong style={{ display: 'block', marginBottom: 4 }}>Project Name</Text>
                  <Text style={{ color: '#64748b' }}>{project?.name || '-'}</Text>
                </div>
                <div>
                  <Text strong style={{ display: 'block', marginBottom: 4 }}>Description</Text>
                  <Text style={{ color: '#64748b' }}>{project?.description || 'No description provided'}</Text>
                </div>
                <Row gutter={16}>
                  <Col span={12}>
                    <Text strong style={{ display: 'block', marginBottom: 4 }}>Status</Text>
                    <Text style={{ color: '#64748b' }}>
                      {project?.status
                        ? project.status.split('_').map(word => word.charAt(0) + word.slice(1).toLowerCase()).join(' ')
                        : '-'}
                    </Text>
                  </Col>
                  <Col span={12}>
                    <Text strong style={{ display: 'block', marginBottom: 4 }}>Priority</Text>
                    <Text style={{ color: '#64748b' }}>
                      {project?.priority ? startCase(project.priority.toLowerCase()) : '-'}
                    </Text>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col span={12}>
                    <Text strong style={{ display: 'block', marginBottom: 4 }}>Start Date</Text>
                    <Text style={{ color: '#64748b' }}>
                      {project?.startDate ? moment(project.startDate).format('MMM D, YYYY') : '-'}
                    </Text>
                  </Col>
                  <Col span={12}>
                    <Text strong style={{ display: 'block', marginBottom: 4 }}>End Date</Text>
                    <Text style={{ color: '#64748b' }}>
                      {project?.endDate ? moment(project.endDate).format('MMM D, YYYY') : '-'}
                    </Text>
                  </Col>
                </Row>
              </div>
            )}
          </Form>
        </Card>

        {/* Team Members Card */}
        <Card
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text strong style={{ fontSize: '16px' }}>Team Members</Text>
              {!isEditingTeam ? (
                <EditOutlined
                  style={{ cursor: 'pointer', fontSize: '16px', color: '#3b82f6' }}
                  onClick={() => setIsEditingTeam(true)}
                />
              ) : (
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button
                    type="primary"
                    onClick={handleSaveTeam}
                    disabled={isSavingTeam}
                    style={{
                      fontSize: '13px',
                      height: '30px',
                      borderRadius: '5px',
                      backgroundColor: '#3b82f6',
                      color: 'white',
                    }}
                  >
                    {isSavingTeam && <LoadingOutlined />} Save
                  </Button>
                  <Button
                    onClick={() => {
                      setIsEditingTeam(false);
                      form.setFieldsValue({
                        team: project.team ? project.team.map((member) => member.id) : [],
                      });
                    }}
                    disabled={isSavingTeam}
                    style={{
                      fontSize: '13px',
                      height: '30px',
                      borderRadius: '5px',
                      color: 'black',
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </div>
          }
          style={{ borderRadius: '7px' }}
        >
          <Form form={form} layout="vertical">
            {isEditingTeam ? (
              <Form.Item label="Select Team Members" name="team">
                <Select
                  mode="multiple"
                  placeholder="Select team members"
                  loading={basicUsersDetailsLoading}
                  onSearch={fetchUsers}
                  onFocus={() => fetchUsers()}
                  filterOption={false}
                  style={{ width: '100%' }}
                >
                  {basicUsersDetails.map((user) => (
                    <Option key={user.id} value={user.id}>
                      {startCase(user.name || user.username)}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            ) : null}

            <div>
              {project && project.team && project.team.length > 0 ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                  {project.team.map((member) => (
                    <Tooltip key={member.id} title={startCase(member.name || member.username)}>
                      <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                        <AvatarS3
                          imgKey={member.avatar && member.avatar.key}
                          username={member.name || member.username}
                          avatarProps={{ size: 40 }}
                        />
                      </div>
                    </Tooltip>
                  ))}
                </div>
              ) : (
                <Text style={{ color: '#64748b' }}>No team members assigned</Text>
              )}
            </div>
          </Form>
        </Card>
      </div>
    </Modal>
  );
};

export default ProjectSettingsModal;
